import { useEffect, useState } from 'react'
import type { Branding, BrandingChange } from './api'
import { translate, type Lang } from './i18n'
import { AgencyBadge, badgeOf, Wordmark } from './SignIn'
import { BADGE_TYPES, isColor, MAX_BADGE_BYTES, readabilityProblem, THEME_PRESETS } from './theme'

/**
 * El tema de la agencia (§16.3d): nombre, lema, colores y escudo con que PROTECT se presenta ante
 * la gente del departamento, en el ingreso y en la barra de arriba.
 *
 * El diseño es de PROTECT; esto es lo que cambia de un departamento a otro. Se ve cómo queda antes
 * de guardarlo, y lo que no se leería (un botón oscuro sobre fondo oscuro) no se deja guardar.
 */
export function AgencyTheme({ lang, marca, busy, onSave, onSaveBadge, onRemoveBadge }: {
  lang: Lang
  /** La marca vigente; nula mientras carga. */
  marca: Branding | null
  busy: boolean
  /** Si se guardó; si no, el motivo lo muestra la pantalla como cualquier rechazo. */
  onSave: (change: BrandingChange) => Promise<boolean>
  onSaveBadge: (image: File) => Promise<boolean>
  onRemoveBadge: () => Promise<boolean>
}) {
  const vigente = (): BrandingChange => ({
    displayName: marca?.agencyName ?? '', motto: marca?.motto ?? '',
    primaryColor: marca?.primaryColor ?? '#0A1628', accentColor: marca?.accentColor ?? '#1EB6FF',
    unitSystem: marca?.unitSystemChosen ?? '', demoNotice: marca?.demoNotice ?? '',
  })
  const [form, setForm] = useState<BrandingChange>(vigente)
  const [guardado, setGuardado] = useState(false)
  const [archivo, setArchivo] = useState<File | null>(null)
  const [vistaArchivo, setVistaArchivo] = useState<string | null>(null)
  const [archivoMalo, setArchivoMalo] = useState<string | null>(null)
  // El selector de archivo no se vacía solo: se vuelve a montar al subir o descartar.
  const [entrada, setEntrada] = useState(0)
  const soltar = () => { setArchivo(null); setEntrada((n) => n + 1) }

  // Cuando llega la marca (o cambia porque se guardó), el formulario parte de ella.
  useEffect(() => {
    if (!marca) return
    setForm({ displayName: marca.agencyName, motto: marca.motto, primaryColor: marca.primaryColor, accentColor: marca.accentColor,
      unitSystem: marca.unitSystemChosen ?? '', demoNotice: marca.demoNotice ?? '' })
  }, [marca])

  useEffect(() => {
    if (!archivo) { setVistaArchivo(null); return }
    const url = URL.createObjectURL(archivo)
    setVistaArchivo(url)
    return () => URL.revokeObjectURL(url)
  }, [archivo])

  if (!marca) return null

  const problema = readabilityProblem(form.primaryColor, form.accentColor)
  const cambiado = form.displayName.trim() !== marca.agencyName || form.motto.trim() !== marca.motto
    || form.primaryColor.toUpperCase() !== marca.primaryColor || form.accentColor.toUpperCase() !== marca.accentColor
    || (form.unitSystem ?? '') !== (marca.unitSystemChosen ?? '')
    || (form.demoNotice ?? '').trim() !== (marca.demoNotice ?? '')
  const cambia = (parte: Partial<BrandingChange>) => { setForm({ ...form, ...parte }); setGuardado(false) }

  const elegir = (f: File | null) => {
    setArchivoMalo(null)
    setArchivo(null)
    if (!f) return
    // Lo mismo que exige el servidor, dicho antes de subirlo.
    if (!BADGE_TYPES.includes(f.type)) { setArchivoMalo(translate(lang, 'theme.badgeType')); return }
    if (f.size > MAX_BADGE_BYTES) { setArchivoMalo(translate(lang, 'theme.badgeSize', [Math.ceil(f.size / 1024)])); return }
    setArchivo(f)
  }

  const escudo = vistaArchivo ?? badgeOf(marca)
  const estilo = { '--brand-bg': form.primaryColor, '--brand-accent': form.accentColor } as React.CSSProperties

  return (
    <section className="panel">
      <h2>{translate(lang, 'theme.title')}</h2>
      <p className="notice">{translate(lang, 'theme.help')}</p>

      <div className="theme-editor">
        <div className="theme-form">
          <div className="card">
            <div className="field">
              <label htmlFor="theme-name">{translate(lang, 'theme.name')}</label>
              <input id="theme-name" value={form.displayName} maxLength={80}
                onChange={(e) => cambia({ displayName: e.target.value })} />
              <span className="meta">{translate(lang, 'theme.nameHelp')}</span>
            </div>
            <div className="field">
              <label htmlFor="theme-motto">{translate(lang, 'theme.motto')}</label>
              <input id="theme-motto" value={form.motto} maxLength={60}
                placeholder={translate(lang, 'theme.mottoExample')}
                onChange={(e) => cambia({ motto: e.target.value })} />
            </div>
            {/* Agencia de demostración: el aviso sale en la web y en la tablet. Vacío en una agencia real. */}
            <div className="field">
              <label htmlFor="theme-demo">{translate(lang, 'theme.demoNotice')}</label>
              <input id="theme-demo" value={form.demoNotice ?? ''} maxLength={120}
                placeholder={translate(lang, 'theme.demoNoticeExample')}
                onChange={(e) => cambia({ demoNotice: e.target.value })} />
              <span className="meta">{translate(lang, 'theme.demoNoticeHelp')}</span>
            </div>
            {/* Cómo se leen las distancias (H-64). Todo se guarda en metros: esto solo cambia la lectura. */}
            <div className="field">
              <label htmlFor="theme-units">{translate(lang, 'theme.units')}</label>
              <select id="theme-units" value={form.unitSystem ?? ''}
                onChange={(e) => cambia({ unitSystem: e.target.value as BrandingChange['unitSystem'] })}>
                <option value="">{translate(lang, 'theme.unitsAuto', [translate(lang, `theme.units.${marca.unitSystem ?? 'metric'}`)])}</option>
                <option value="imperial">{translate(lang, 'theme.units.imperial')}</option>
                <option value="metric">{translate(lang, 'theme.units.metric')}</option>
              </select>
            </div>
          </div>

          <div className="card">
            <h3>{translate(lang, 'theme.presets')}</h3>
            <div className="theme-presets">
              {THEME_PRESETS.map((t) => {
                const elegido = t.primaryColor === form.primaryColor.toUpperCase() && t.accentColor === form.accentColor.toUpperCase()
                return (
                  <button key={t.id} className={`theme-preset ${elegido ? 'selected' : ''}`} aria-pressed={elegido}
                    onClick={() => cambia({ primaryColor: t.primaryColor, accentColor: t.accentColor })}>
                    <span className="theme-swatch" style={{ background: t.primaryColor }}>
                      <span style={{ background: t.accentColor }} />
                    </span>
                    {translate(lang, `theme.preset.${t.id}`)}
                  </button>
                )
              })}
            </div>

            <div className="theme-colors">
              <ColorField id="theme-primary" label={translate(lang, 'theme.primary')} help={translate(lang, 'theme.primaryHelp')}
                value={form.primaryColor} onChange={(v) => cambia({ primaryColor: v })} />
              <ColorField id="theme-accent" label={translate(lang, 'theme.accent')} help={translate(lang, 'theme.accentHelp')}
                value={form.accentColor} onChange={(v) => cambia({ accentColor: v })} />
            </div>
            {problema && <p className="error" role="alert">{translate(lang, `theme.problem.${problema}`)}</p>}
          </div>

          <div className="actions">
            <button className="primary" disabled={busy || !cambiado || problema !== null}
              onClick={() => void onSave({ ...form, displayName: form.displayName.trim(), motto: form.motto.trim(), demoNotice: (form.demoNotice ?? '').trim() })
                .then((ok) => setGuardado(ok))}>
              {translate(lang, 'theme.save')}
            </button>
            <button disabled={busy || !cambiado} onClick={() => { setForm(vigente()); setGuardado(false) }}>
              {translate(lang, 'theme.discard')}
            </button>
            {guardado && !cambiado && <span className="chip ok">{translate(lang, 'theme.saved')}</span>}
          </div>

          <div className="card">
            <h3>{translate(lang, 'theme.badge')}</h3>
            <p className="meta">{translate(lang, 'theme.badgeHelp')}</p>
            <div className="actions">
              <input key={entrada} type="file" accept={BADGE_TYPES.join(',')} aria-label={translate(lang, 'theme.badgeChoose')}
                onChange={(e) => elegir(e.target.files?.[0] ?? null)} />
            </div>
            {archivoMalo && <p className="error">{archivoMalo}</p>}
            <div className="actions">
              <button className="primary" disabled={busy || !archivo}
                onClick={() => archivo && void onSaveBadge(archivo).then((ok) => { if (ok) soltar() })}>
                {translate(lang, 'theme.badgeUpload')}
              </button>
              {archivo && <button disabled={busy} onClick={soltar}>{translate(lang, 'theme.discard')}</button>}
              {marca.hasBadge && !archivo && (
                <button className="danger" disabled={busy} onClick={() => void onRemoveBadge()}>
                  {translate(lang, 'theme.badgeRemove')}
                </button>
              )}
            </div>
            {!marca.hasBadge && !archivo && <p className="meta">{translate(lang, 'theme.badgeNone')}</p>}
          </div>
        </div>

        {/* Cómo queda, antes de guardarlo: el lado derecho del ingreso y la barra de arriba. */}
        <figure className="theme-preview" style={estilo} aria-label={translate(lang, 'theme.preview')}>
          <figcaption>{translate(lang, 'theme.preview')}</figcaption>
          <div className="theme-preview-screen">
            <div className="theme-preview-brand"><Wordmark /></div>
            <AgencyBadge name={form.displayName || marca.agencyName} src={escudo} />
            {(form.displayName || marca.agencyName) && <div className="signin-agency">{form.displayName || marca.agencyName}</div>}
            {form.motto && <div className="signin-motto">{form.motto}</div>}
            <div className="signin-title">{translate(lang, 'signin.authorized')}</div>
            <div className="signin-primary" aria-hidden="true">{translate(lang, 'signin.sso')} →</div>
          </div>
          <div className="theme-preview-bar">
            <AgencyBadge name={form.displayName} src={escudo} className="topbar-badge" />
            <span>{form.displayName || marca.agencyName}</span>
          </div>
        </figure>
      </div>
    </section>
  )
}

function ColorField({ id, label, help, value, onChange }: {
  id: string; label: string; help: string; value: string; onChange: (v: string) => void
}) {
  // El texto se deja escribir a medias («#1E»); el selector solo recibe un color completo.
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="theme-color">
        <input type="color" value={isColor(value) ? value : '#000000'} aria-label={label}
          onChange={(e) => onChange(e.target.value.toUpperCase())} />
        <input id={id} value={value} maxLength={7} spellCheck={false}
          onChange={(e) => onChange(e.target.value.trim().toUpperCase())} />
      </div>
      <span className="meta">{help}</span>
    </div>
  )
}
